import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Toast } from 'primereact/toast';
import { useNavigate, useParams } from 'react-router-dom';
import BreakModal from './breakModal';
import BreakDeleteConfirmationModal from './deleteConfirm';
import BreaksAccordion from './breakAccordion';
import SubshiftEditModal from './subShiftModal';

export default function Subshift() {
    const [shift, setShift] = useState(null);
    const [breaks, setBreaks] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [isEditBreakMode, setIsEditBreakMode] = useState(false);
    const [selectedBreak, setSelectedBreak] = useState(null);
    const [showBreakModal, setShowBreakModal] = useState(false);
    const navigate = useNavigate();
    const toast = useRef(null);
    const { id } = useParams();
    const newId = Number(id);

    // Access permissions array from Redux store
    const permissions = useSelector((state) => state.emp.permissions);

    const pagePermissions = ["editshiftsID"];

    const fetchShiftDetails = useCallback(async () => {
        const token = localStorage.getItem("accessToken");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API}/api/employee/shiftlines/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const data = response.data;
            setShift(data);
            setBreaks(data.shiftline_breaks || []);
        } catch (error) {
            console.error("Error fetching shift details:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch shift details. Please try again.',
                life: 3000,
            });
        }
    }, [id]);

    useEffect(() => {
        fetchShiftDetails();
    }, [fetchShiftDetails]);

    const handleEditClick = () => {
        setShowEditModal(true);
    };

    const handleAddBreak = async (breakData, id) => {
        const token = localStorage.getItem("accessToken");
        breakData.shiftline = newId;
        if (isEditBreakMode) {
            try {
                const response = await axios.put(
                    `${process.env.REACT_APP_API}/api/employee/breaks/${id}/`,
                    breakData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Break updated successfully!',
                    life: 3000,
                });
                handleCloseBreakModal();
                fetchShiftDetails();
            } catch (error) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error updating break. Please try again.',
                    life: 3000,
                });
                console.error("Error updating break:", error);
            }
        } else {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_API}/api/employee/breaks/`,
                    { ...breakData, shiftline: newId },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Break added successfully!',
                    life: 3000,
                });
                handleCloseBreakModal();
                fetchShiftDetails();
            } catch (error) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error adding break. Please try again.',
                    life: 3000,
                });
                console.error("Error adding break:", error);
            }
        }
    };

    const handleOpenBreakModal = () => {
        setIsEditBreakMode(false);
        setSelectedBreak(null);
        setShowBreakModal(true);
    };

    const handleDeleteClick = () => {
        if (shift?.employee_count > 0) {
            toast.current.show({
                severity: 'warn',
                summary: 'Cannot Delete Sub-Shift',
                detail: 'This sub-shift has associated employees. Please reassign them before deleting.',
                life: 3000,
            });
        } else {
            setOpenDeleteModal(true);
        }
    };

    const handleCloseBreakModal = () => {
        setShowBreakModal(false);
        setIsEditBreakMode(false);
        setSelectedBreak(null);
    };

    const handleEditBreak = (breakItem) => {
        setIsEditBreakMode(true);
        setSelectedBreak(breakItem);
        setShowBreakModal(true);
    };

    const handleDeleteBreak = async (breakId) => {
        const token = localStorage.getItem("accessToken");
        try {
            await axios.delete(
                `${process.env.REACT_APP_API}/api/employee/breaks/${breakId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Break deleted successfully!',
                life: 3000,
            });
            fetchShiftDetails();
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error deleting break. Please try again.',
                life: 3000,
            });
            console.error("Error deleting break:", error);
        }
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    const handleConfirmDelete = async () => {
        const token = localStorage.getItem('accessToken');
        try {
            await axios.delete(
                `${process.env.REACT_APP_API}/api/employee/shiftlines/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setOpenDeleteModal(false);
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Sub-Shift Deleted Successfully',
                life: 3000,
            });
            setTimeout(() => {
                navigate('/user/shifts/');
            }, 3000);
        } catch (error) {
            console.error('Error deleting sub-shift:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete sub-shift',
                life: 3000,
            });
        }
    };

    return (
        <div className="singlePositionContainer px-4 pt-4 w-100">
            <Toast ref={toast} position="bottom-right" />
            <h4 className="text-capitalize">
                {shift?.shift_name || 'N/A'} - {shift?.shift_type || 'N/A'}
            </h4>

            <table>
                <tr>
                    <td
                        className="mb-0 fw-bolder ps-0"
                        style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
                    >
                        Working Hours
                    </td>
                    <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
                        <span className="me-4">:</span>
                        {shift?.working_hours || 'N/A'}
                    </td>
                </tr>
                <tr>
                    <td
                        className="mb-0 fw-bolder ps-0"
                        style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
                    >
                        Start Time
                    </td>
                    <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
                        <span className="me-4">:</span>
                        {shift?.start_time || 'N/A'}
                    </td>
                </tr>
                <tr>
                    <td
                        className="mb-0 fw-bolder ps-0"
                        style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
                    >
                        End Time
                    </td>
                    <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
                        <span className="me-4">:</span>
                        {shift?.end_time || 'N/A'}
                    </td>
                </tr>
                <tr>
                    <td
                        className="mb-0 fw-bolder ps-0"
                        style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
                    >
                        Cut Off Time
                    </td>
                    <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
                        <span className="me-4">:</span>
                        {shift?.cut_off_time || 'N/A'}
                    </td>
                </tr>
                <tr>
                    <td
                        className="mb-0 fw-bolder ps-0"
                        style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
                    >
                        Leave Type
                    </td>
                    <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
                        <span className="me-4">:</span>
                        {shift?.leave_type || 'N/A'}
                    </td>
                </tr>
                <tr>
                    <td
                        className="mb-0 fw-bolder ps-0"
                        style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
                    >
                        Priority
                    </td>
                    <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
                        <span className="me-4">:</span>
                        {shift?.priority || 'N/A'}
                    </td>
                </tr>
            </table>

            <div className="employeeTableContainer mt-4 w-100">
                <div className="d-flex justify-content-end mb-3 gap-2 gap-md-3">
                    {permissions.includes('editshiftsID') && (
                        <button
                            className="btn btn-outline-success"
                            onClick={handleOpenBreakModal}
                        >
                            Add Break <IconPlus stroke={2} />
                        </button>
                    )}
                    {permissions.includes('editshiftsID') && (
                        <button
                            className="btn btn-outline-primary"
                            onClick={handleEditClick}
                        >
                            <IconEdit stroke={2} />
                        </button>
                    )}
                    {permissions.includes('editshiftsID') && (
                        <button
                            className="btn btn-outline-danger"
                            onClick={handleDeleteClick}
                        >
                            <IconTrash stroke={2} />
                        </button>
                    )}
                </div>
            </div>

            {permissions.includes('editshiftsID') && (
                breaks.length > 0 ? (
                    <BreaksAccordion
                        breaks={breaks}
                        onEditBreak={handleEditBreak}
                        onDeleteBreak={handleDeleteBreak}
                        header={'Breaks'}
                    />
                ) : (
                    <p>No breaks found.</p>
                )
            )}

            {permissions.includes('editshiftsID') && showEditModal && (
                <SubshiftEditModal
                    open={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    shiftId={id}
                    onUpdateSuccess={() => {
                        fetchShiftDetails();
                    }}
                    toast={toast}
                />
            )}

            {permissions.includes('editshiftsID') && showBreakModal && (
                <BreakModal
                    showModal={showBreakModal}
                    onClose={handleCloseBreakModal}
                    onSubmit={handleAddBreak}
                    initialBreak={selectedBreak}
                    isEditMode={isEditBreakMode}
                />
            )}

            {permissions.includes('editshiftsID') && (
                <BreakDeleteConfirmationModal
                    open={openDeleteModal}
                    onClose={handleCloseDeleteModal}
                    onConfirm={handleConfirmDelete}
                    breakName="Sub-Shift"
                />
            )}
        </div>
    );
}